import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import {
  BulkMoveToTrashMediaHouseDocument,
  type BulkMoveToTrashMediaHouseMutation,
  type BulkMoveToTrashMediaHouseMutationVariables,
  GetMediaHouseImagesDocument,
  type MediaEdge,
  type MediaHouseMedia,
} from "@/generated/graphql";
import { Dnd } from "@/lib/dnd/components/Dnd";
import { useMediaHandler } from "@/lib/hooks/slides/editor/useMediaHandler";
import { Button } from "@ai-teach/ui-web";
import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiAlertTriangle,
  FiCheck,
  FiImage,
  FiRefreshCw,
  FiUploadCloud,
  FiZap,
} from "react-icons/fi";
import { DisabledLockOverlay } from "../DisabledLockOverlay";
import { MediaPropertiesDialog } from "./modals/MediaPropertiesDialog";
import { MediaUploadModal } from "./modals/MediaUploadModal";
import { MoveMediaModal } from "./modals/MoveMediaModal";
import { RenameMediaModal } from "./modals/RenameMediaModal";
import { TextToImageModal } from "./modals/TextToImageModal";
import { FoldersSection } from "./sections/FoldersSection";
import { FloatingActionBar } from "./shared/FloatingActionBar";
import { ThreeDotMenu } from "./shared/ThreeDotMenu";

interface ImageTabProps {
  disabled?: boolean;
}

const isServerError = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false;
  const error = err as { networkError?: unknown; graphQLErrors?: unknown[] };
  if (error.networkError) return true;
  const serverErrorCodes = ["INTERNAL_SERVER_ERROR", "SERVICE_UNAVAILABLE"];
  return (
    error.graphQLErrors?.some((e: unknown) => {
      const graphQLError = e as { extensions?: { code?: string } };
      return serverErrorCodes.includes(graphQLError.extensions?.code as string);
    }) ?? false
  );
};

export function ImageTab({ disabled = false }: ImageTabProps) {
  const { setActivePanel } = useSlideEditorContext();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<Map<string, MediaHouseMedia>>(new Map());
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showPropertiesDialog, setShowPropertiesDialog] = useState(false);
  const [showTextToImageModal, setShowTextToImageModal] = useState(false);
  const [activeMedia, setActiveMedia] = useState<MediaHouseMedia | null>(null);
  const [itemsToMove, setItemsToMove] = useState<MediaHouseMedia[]>([]);

  const observerTarget = useRef<HTMLDivElement>(null);
  const { addImageToSlide, copying } = useMediaHandler();

  const { data, loading, error, fetchMore, refetch } = useQuery(GetMediaHouseImagesDocument, {
    variables: { folderId: currentFolderId, first: 20 },
    fetchPolicy: "cache-and-network",
    skip: disabled,
  });

  const [bulkDelete] = useMutation<
    BulkMoveToTrashMediaHouseMutation,
    BulkMoveToTrashMediaHouseMutationVariables
  >(BulkMoveToTrashMediaHouseDocument, {
    refetchQueries: ["GetMediaHouseImages"],
    onCompleted: (data) => {
      toast.success(
        `Moved ${data.bulkMoveToTrashMediaHouse} ${data.bulkMoveToTrashMediaHouse === 1 ? "item" : "items"} to trash`
      );
      setSelectedMedia(new Map());
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const handleImageClick = async (mediaHouseMedia: MediaHouseMedia) => {
    await addImageToSlide(mediaHouseMedia.id);
    setActivePanel(null);
  };

  const handleToggleSelect = (id: string, media: MediaHouseMedia) => {
    setSelectedMedia((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, media);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedMedia(new Map());
  const handleRename = (item: MediaHouseMedia) => {
    setActiveMedia(item);
    setShowRenameModal(true);
  };
  const handleMove = (items: MediaHouseMedia[]) => {
    setItemsToMove(items);
    setShowMoveModal(true);
  };
  const handleDelete = async (items: MediaHouseMedia[]) => {
    if (!confirm(`Move ${items.length} ${items.length === 1 ? "item" : "items"} to trash?`)) return;
    await bulkDelete({ variables: { ids: items.map((item) => item.id) } });
  };
  const handleProperties = (item: MediaHouseMedia) => {
    setActiveMedia(item);
    setShowPropertiesDialog(true);
  };

  const selectedMediaArray = Array.from(selectedMedia.values());
  const selectedIds = new Set(selectedMedia.keys());
  const images = data?.mediaHouseMedia.edges || [];
  const hasSelection = selectedMedia.size > 0;
  const pageInfo = data?.mediaHouseMedia?.pageInfo;
  const hasMore = pageInfo?.hasNextPage || false;

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (!target) return;
      if (target.isIntersecting && hasMore && !loading) {
        fetchMore({ variables: { after: pageInfo?.endCursor } });
      }
    },
    [hasMore, loading, fetchMore, pageInfo?.endCursor]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <div className="pt-2 pb-3 relative">
      {/* Folders Section */}
      <FoldersSection
        parentId={currentFolderId}
        mediaType="image"
        onFolderClick={setCurrentFolderId}
        disabled={disabled}
      />

      {/* Scrollable Media Container */}
      <div
        className={`max-h-[calc(100vh-300px)] overflow-y-auto pr-0.5 gradient-scrollbar ${
          disabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
        }`}
      >
        {/* Action Buttons */}
        <div className="flex gap-2 mb-3">
          <Button
            onClick={() => setShowTextToImageModal(true)}
            variant="primary"
            size="sm"
            disabled={disabled}
            leftIcon={<FiZap className="h-3.5 w-3.5" />}
            className="flex-1"
          >
            Generate
          </Button>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="secondary"
            size="sm"
            disabled={disabled}
            leftIcon={<FiUploadCloud className="h-3.5 w-3.5" />}
            className="flex-1"
          >
            Upload
          </Button>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-3 gap-1.5">
          {loading && images.length === 0 ? (
            /* Skeleton loading grid */
            Array.from({ length: 9 }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton tiles
                key={i}
                className="aspect-square rounded-xl bg-slate-200 dark:bg-gray-700 animate-pulse"
                style={{ animationDelay: `${i * 60}ms` }}
              />
            ))
          ) : error ? (
            <div
              className="col-span-3 flex flex-col items-center justify-center py-10 px-4 gap-3"
              role="alert"
            >
              <div className="w-12 h-12 rounded-2xl bg-error-50 dark:bg-error-900/30 flex items-center justify-center">
                <FiAlertTriangle className="text-lg text-error-500" />
              </div>
              <p className="text-xs font-semibold text-slate-600 dark:text-gray-300">
                {isServerError(error) ? "Media House unavailable" : "Failed to load"}
              </p>
              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                {isServerError(error) ? "The server appears to be down." : error.message}
              </p>
              <Button
                onClick={() => refetch()}
                variant="primary"
                size="sm"
                leftIcon={<FiRefreshCw className="h-3 w-3" />}
              >
                Retry
              </Button>
            </div>
          ) : images.length === 0 ? (
            <div className="col-span-3 flex flex-col items-center justify-center py-12 gap-3">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(20,184,166,0.12) 100%)",
                }}
              >
                <FiImage className="text-2xl text-slate-400 dark:text-gray-500" />
              </div>
              <p className="text-xs font-semibold text-slate-500 dark:text-gray-400">
                No images yet
              </p>
              <p className="text-[11px] text-slate-400 dark:text-gray-500 text-center leading-relaxed max-w-[160px]">
                Upload an image or generate one with AI to get started
              </p>
            </div>
          ) : (
            images.map(({ node: image }: MediaEdge) => {
              const isSelected = selectedIds.has(image.id);
              const isHovered = hoveredId === image.id;
              const showOverlay = isHovered || hasSelection;

              return (
                <Dnd
                  id={image.id}
                  key={image.id}
                  data={{
                    id: image.id,
                    type: "image",
                    media: { src: image.url, filename: image.filename },
                  }}
                >
                  <div
                    className={`relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-150 ${
                      isSelected
                        ? "ring-2 ring-primary-500 ring-offset-1 shadow-lg shadow-primary-500/20"
                        : "hover:shadow-md hover:shadow-black/10"
                    }`}
                    onMouseEnter={() => setHoveredId(image.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => !copying && !isSelected && handleImageClick(image)}
                  >
                    <img
                      src={image.url}
                      alt={image.filename}
                      className="w-full aspect-square object-cover"
                      draggable={false}
                    />

                    {/* Selection overlay tint */}
                    {isSelected && (
                      <div className="absolute inset-0 bg-primary-500/20 pointer-events-none" />
                    )}

                    {/* Hover dark overlay */}
                    {!isSelected && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 pointer-events-none transition-colors duration-150" />
                    )}

                    {/* Checkbox — shown on hover or when any selection active */}
                    {showOverlay && (
                      <div className="absolute top-1.5 left-1.5 z-10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleSelect(image.id, image);
                          }}
                          className={`w-5 h-5 rounded-md flex items-center justify-center transition-all duration-150 shadow-sm ${
                            isSelected
                              ? "bg-primary-500 border-primary-500 border"
                              : "bg-white/90 border border-slate-300 hover:border-primary-400"
                          }`}
                        >
                          {isSelected && <FiCheck className="h-3 w-3 text-white" strokeWidth={3} />}
                        </button>
                      </div>
                    )}

                    {/* Three-dot menu */}
                    {!hasSelection && (
                      <div
                        className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ThreeDotMenu
                          media={image}
                          onProperties={() => handleProperties(image)}
                          onMove={() => handleMove([image])}
                          onDelete={() => handleDelete([image])}
                        />
                      </div>
                    )}

                    {/* Filename label on hover */}
                    <div
                      className="absolute bottom-0 left-0 right-0 px-2 py-1.5 translate-y-full group-hover:translate-y-0 transition-transform duration-150"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
                      }}
                    >
                      <p className="text-white text-[10px] font-medium truncate leading-tight">
                        {image.filename}
                      </p>
                    </div>

                    {/* Copying overlay */}
                    {copying && (
                      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                      </div>
                    )}
                  </div>
                </Dnd>
              );
            })
          )}

          {/* Infinite scroll trigger */}
          {hasMore && (
            <div ref={observerTarget} className="col-span-3 h-8 flex items-center justify-center">
              {loading && (
                <div className="w-4 h-4 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Bar */}
      <FloatingActionBar
        selectedItems={selectedMediaArray}
        selectedCount={selectedMedia.size}
        mediaType="image"
        onClearSelection={handleClearSelection}
        onRename={handleRename}
        onMove={handleMove}
        onDelete={handleDelete}
      />

      {/* Modals */}
      {activeMedia && (
        <>
          <RenameMediaModal
            isOpen={showRenameModal}
            onClose={() => {
              setShowRenameModal(false);
              setActiveMedia(null);
            }}
            mediaId={activeMedia.id}
            currentFilename={activeMedia.filename}
            mediaType="image"
          />
          <MediaPropertiesDialog
            isOpen={showPropertiesDialog}
            onClose={() => {
              setShowPropertiesDialog(false);
              setActiveMedia(null);
            }}
            media={activeMedia}
            mediaType="image"
          />
        </>
      )}

      <MoveMediaModal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setItemsToMove([]);
        }}
        mediaIds={itemsToMove.map((m) => m.id)}
        mediaType="image"
        currentFolderId={currentFolderId}
      />

      <TextToImageModal
        isOpen={showTextToImageModal}
        onClose={() => setShowTextToImageModal(false)}
        currentFolderId={currentFolderId}
      />

      <MediaUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        mediaType="image"
        folderId={currentFolderId}
        refetchQuery="GetMediaHouseImages"
      />

      {disabled && (
        <DisabledLockOverlay
          title="Images Not Available"
          description="This slide type doesn't support image elements"
        />
      )}
    </div>
  );
}
